import type { RefObject } from 'react';
import { LoadingState, resolveHttpError } from '@sakhtar/design-system/react';
import type { ChartBaseStateProps } from './types.js';

export interface ChartFrameProps extends ChartBaseStateProps {
  readonly containerRef: RefObject<HTMLDivElement | null>;
  readonly height?: number | undefined;
  readonly ariaLabel: string;
  readonly isEmpty?: boolean | undefined;
}

/**
 * The DOM node every ChartKit component's `useEChart` attaches to, with unified
 * first-class support for loading, honest empty, and error (404, 500, 503) states
 * with Persian messaging and retry button.
 */
export function ChartFrame({
  containerRef,
  height = 260,
  ariaLabel,
  isLoading,
  loadingMessage,
  error,
  status,
  errorMessage,
  onRetry,
  retryLabel = 'تلاش دوباره',
  isEmpty,
  emptyMessage = 'داده‌ای برای نمایش وجود ندارد.',
}: ChartFrameProps) {
  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={ariaLabel}
        style={{
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          color: 'var(--sk-fg-muted)',
          fontSize: 'var(--sk-fs-sm)',
        }}
      >
        <LoadingState label={loadingMessage ?? 'در حال بارگذاری نمودار…'} />
      </div>
    );
  }

  const hasError = Boolean(error || (status && status >= 400));
  if (hasError) {
    let effectiveStatus = status;
    if (effectiveStatus === undefined || effectiveStatus === null) {
      if (typeof error === 'object' && error !== null) {
        if ('status' in error && typeof (error as { status: unknown }).status === 'number') {
          effectiveStatus = (error as { status: number }).status;
        } else if (
          'statusCode' in error &&
          typeof (error as { statusCode: unknown }).statusCode === 'number'
        ) {
          effectiveStatus = (error as { statusCode: number }).statusCode;
        }
      }
    }

    const resolved = resolveHttpError(effectiveStatus, errorMessage);

    if (effectiveStatus === 403) {
      return null;
    }

    return (
      <div
        role="alert"
        aria-label={ariaLabel}
        style={{
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: 16,
          textAlign: 'center',
          color: 'var(--sk-fg-muted)',
          fontSize: 'var(--sk-fs-sm)',
        }}
      >
        <div style={{ fontWeight: 600, color: 'var(--sk-fg)', fontSize: 'var(--sk-fs-base)' }}>
          {resolved.title}
        </div>
        <p style={{ margin: 0, maxWidth: 360 }}>{resolved.description}</p>
        {onRetry ? (
          <button
            type="button"
            className="sak-btn sak-btn--secondary"
            onClick={onRetry}
            style={{ marginTop: 6 }}
          >
            {retryLabel}
          </button>
        ) : null}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div
        role="img"
        aria-label={ariaLabel}
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--sk-fg-muted)',
          fontSize: 'var(--sk-fs-sm)',
          textAlign: 'center',
          padding: 16,
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div ref={containerRef} role="img" aria-label={ariaLabel} style={{ width: '100%', height }} />
  );
}
